import React, { useEffect, useRef, useState } from "react";

const PI_CONSENT_COOKIE = "pi_consent";
const PUSH_SCRIPT_ID = "pi-monetag-push";
const ONCLICK_SCRIPT_ID = "pi-monetag-onclick";
const PUSH_SRC = "https://5gvci.com/act/files/tag.min.js?z=12002278";
const ONCLICK_SRC = "https://al5sm.com/tag.min.js";
const ONCLICK_ZONE = "12002279";
const INPAGE_SCRIPT_ID = "pi-monetag-inpage-push";
const INPAGE_SRC = "https://nap5k.com/tag.min.js";
const INPAGE_ZONE = "12002362";
const VIGNETTE_SCRIPT_ID = "pi-monetag-vignette";
const VIGNETTE_ZONE = "12002408";

function readMarketingFromCookie() {
    if (typeof document === "undefined") return null;
    try {
        const match = document.cookie.match(
            new RegExp(`(?:^|; )${PI_CONSENT_COOKIE}=([^;]*)`),
        );
        if (!match) return null;
        const parsed = JSON.parse(decodeURIComponent(match[1]));
        if (parsed && typeof parsed.m === "boolean") {
            return parsed.m;
        }
    } catch {
        /* ignore */
    }
    return null;
}

function readMarketingConsent() {
    if (typeof window === "undefined") return false;
    const PI = window.PIConsent;
    if (PI && typeof PI.getState === "function") {
        const state = PI.getState();
        if (state && state.decided) {
            return !!state.marketing;
        }
    }
    if (PI && typeof PI.has === "function" && PI.has("marketing")) {
        return true;
    }
    return readMarketingFromCookie() === true;
}

function removeScript(id) {
    const existing = document.getElementById(id);
    if (existing) {
        existing.remove();
    }
}

/** Monetag pins its banner iframe on documentElement, so it survives route changes. */
export function clearMonetagPageAds() {
    [
        INPAGE_SCRIPT_ID,
        PUSH_SCRIPT_ID,
        ONCLICK_SCRIPT_ID,
        VIGNETTE_SCRIPT_ID,
    ].forEach(removeScript);
    if (typeof document === "undefined") return;
    document.documentElement.querySelectorAll(":scope > iframe").forEach((frame) => {
        frame.remove();
    });
}

function ensureOnclickScript(slot) {
    if (!slot || document.getElementById(ONCLICK_SCRIPT_ID)) return;
    const script = slot.appendChild(document.createElement("script"));
    script.id = ONCLICK_SCRIPT_ID;
    script.dataset.zone = ONCLICK_ZONE;
    script.src = ONCLICK_SRC;
}

function ensureZoneScript(id, zone, src) {
    if (!src || document.getElementById(id)) return;
    const parent = [document.documentElement, document.body].filter(Boolean).pop();
    if (!parent) return;
    const script = parent.appendChild(document.createElement("script"));
    script.id = id;
    script.dataset.zone = zone;
    script.src = src;
}

function ensurePushScript(slot) {
    if (!slot || slot.querySelector("script")) return;
    const script = document.createElement("script");
    script.id = PUSH_SCRIPT_ID;
    script.async = true;
    script.setAttribute("data-cfasync", "false");
    script.src = PUSH_SRC;
    slot.appendChild(script);
}

function useMarketingConsent() {
    const [allowed, setAllowed] = useState(readMarketingConsent);

    useEffect(() => {
        const sync = () => setAllowed(readMarketingConsent());
        sync();
        if (typeof window === "undefined") return undefined;
        window.PIConsent?.onConsent?.("marketing", sync);
        window.addEventListener("pi-consent-update", sync);
        return () => window.removeEventListener("pi-consent-update", sync);
    }, []);

    return allowed;
}

/** Push notification tag. Place at the bottom of the main page. `force` skips marketing consent. */
export function MonetagPushTag({ force = false }) {
    const slotRef = useRef(null);
    const allowed = useMarketingConsent();
    const show = force || allowed;

    useEffect(() => {
        const slot = slotRef.current;
        if (!slot) return undefined;
        if (!show) {
            removeScript(PUSH_SCRIPT_ID);
            return undefined;
        }
        ensurePushScript(slot);
        return () => removeScript(PUSH_SCRIPT_ID);
    }, [show]);

    return <div ref={slotRef} className="pi-monetag-push" />;
}

/** Click ad, zone 12002279. Test page only. `force` skips marketing consent. */
export function MonetagOnclickTag({ force = false }) {
    const slotRef = useRef(null);
    const allowed = useMarketingConsent();
    const show = force || allowed;

    useEffect(() => {
        const slot = slotRef.current;
        if (!slot) return undefined;
        if (!show) {
            removeScript(ONCLICK_SCRIPT_ID);
            return undefined;
        }
        ensureOnclickScript(slot);
        return () => removeScript(ONCLICK_SCRIPT_ID);
    }, [show]);

    return <div ref={slotRef} className="pi-monetag-onclick" />;
}

/** In-page push banner. The best tag, zone 12002362. `force` skips marketing consent. */
export function MonetagInpagePushTag({ force = false }) {
    const allowed = useMarketingConsent();
    const show = force || allowed;

    useEffect(() => {
        if (!show) {
            removeScript(INPAGE_SCRIPT_ID);
            return undefined;
        }
        ensureZoneScript(INPAGE_SCRIPT_ID, INPAGE_ZONE, INPAGE_SRC);
        return () => clearMonetagPageAds();
    }, [show]);

    return null;
}

/**
 * Vignette banner, Amazing tag, zone 12002408. Test page only.
 * Pass the script URL from Monetag's tag code. Nothing loads until `src` is set.
 */
export function MonetagVignetteTag({ src = "" }) {
    useEffect(() => {
        ensureZoneScript(VIGNETTE_SCRIPT_ID, VIGNETTE_ZONE, src);
        return () => removeScript(VIGNETTE_SCRIPT_ID);
    }, [src]);

    return null;
}
